import { AxiosError } from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { API, QUERY_KEYS } from '@config';
import { useRequest, useDisplayResponseMessage } from '@hooks';

type ConnectionsResponse = {
  connections: Connection[];
};

export const useConnectionsQuery = (organizationUuid?: string) => {
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();

  return useQuery({
    enabled: !!organizationUuid,
    queryKey: [
      QUERY_KEYS.CONNECTIONS,
      QUERY_KEYS.ORGANIZATION_CONNECTIONS,
      organizationUuid,
    ],
    queryFn: async () => {
      try {
        const { data } = await request<ConnectionsResponse>(
          API.CORE_ORGANIZATIONS_CONNECTIONS(organizationUuid as string),
        );
        return data.connections;
      } catch (error) {
        // Reported here rather than from the page's render body: antd's message
        // API is a state update, and React discards one issued during another
        // component's render ("Cannot update a component while rendering a
        // different component"), so the toast never appeared.
        displayResponseMessage('connections.fetch.error');
        throw error;
      }
    },
  });
};

export const useCreateConnectionMutation = () => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return useMutation({
    mutationFn: async (data: {
      nodetype: string;
      organization: string;
      name: string;
      data: { [key: string]: string };
      slug?: string;
      environment?: string;
    }) => {
      await request(API.CONNECTIONS_CREATE(), {
        method: 'POST',
        data,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONNECTIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_CONNECTIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.NODE_CONNECTION_OPTIONS,
          variables.nodetype,
          variables.organization,
        ],
      });
      displayResponseMessage('connections.create.success');
    },
    onError: (error) => {
      console.error(error);
      displayResponseMessage('connections.create.error');
    },
  });
};

export const useUpdateConnectionMutation = () => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return useMutation({
    mutationFn: async ({
      connectionUuid,
      data,
    }: {
      connectionUuid: string;
      data: {
        name: string;
        data: { [key: string]: unknown };
        nodetype?: string;
        slug?: string | null;
        environment?: string;
      };
    }) => {
      await request(API.CONNECTIONS_UPDATE(connectionUuid), {
        method: 'PATCH',
        data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONNECTIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_CONNECTIONS],
      });
      displayResponseMessage('connections.update.success');
    },
    onError: (error) => {
      console.error(error);
      displayResponseMessage('connections.update.error');
    },
  });
};

export const useDeleteConnectionMutation = () => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return useMutation({
    mutationFn: async (connection: Connection) => {
      await request(API.CONNECTIONS_DELETE(connection.uuid), {
        method: 'DELETE',
      });
      return connection;
    },
    onSuccess: (connection) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONNECTIONS],
      });
      displayResponseMessage('connections.delete.success', connection);
    },
    onError: (error, connection) => {
      console.error(error);
      displayResponseMessage('connections.delete.error', connection);
    },
  });
};

export const useVerifyConnectionMutation = () => {
  const request = useRequest();
  const displayResponseMessage = useDisplayResponseMessage();

  return useMutation({
    mutationFn: async (connection: Connection) => {
      await request(API.CONNECTIONS_VERIFY(connection.uuid), {
        method: 'POST',
      });
      return connection;
    },
    onSuccess: (connection) => {
      displayResponseMessage('connections.verify.success', connection);
    },
    onError: (error, connection) => {
      console.error(error);
      if (error instanceof AxiosError) {
        displayResponseMessage(
          'connections.verify.error',
          connection,
          error.response?.data?.error_text,
        );
      }
    },
  });
};

export const useDisconnectConnectionMutation = () => {
  const request = useRequest();
  const queryClient = useQueryClient();
  const displayResponseMessage = useDisplayResponseMessage();

  return useMutation({
    mutationFn: async (connection: Connection) => {
      await request(API.CONNECTIONS_DISCONNECT(connection.uuid), {
        method: 'POST',
      });
      return connection;
    },
    onSuccess: (connection) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_CONNECTIONS],
      });
      displayResponseMessage('connections.disconnect.success', connection);
    },
    onError: (error, connection) => {
      console.error(error);
      displayResponseMessage('connections.disconnect.error', connection);
    },
  });
};
